import { acquireAccessToken } from '../auth-provider';
import { API_BASE_URL } from './config';

// Normalize URL by removing leading slashes
function normalizeUrl(url: string): string {
  return url.replace(/^\/+/, '');
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

// Type for API errors
interface ApiError extends Error {
  status?: number;
  [key: string]: any;
}

async function getAuthHeaders(isFormData: boolean = false): Promise<HeadersInit> {
  try {
    const token = await acquireAccessToken();
    
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
    };
    
    // Only add Content-Type for JSON requests, not for FormData
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  } catch (error) {
    console.error('Failed to acquire token:', error);
    throw error;
  }
}

async function handleResponse(response: Response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    
    // If we have error data from server, enhance it with status and throw
    if (errorData) {
      const apiError = errorData as ApiError;
      apiError.status = response.status;
      throw apiError;
    }
    
    // Fallback for non-JSON errors
    const error = new Error(`HTTP error! status: ${response.status}`) as ApiError;
    error.status = response.status;
    throw error;
  }
  
  // Return null for 204 No Content or empty response
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }
  
  // For other successful responses with content, try to parse as JSON
  return response.json();
}

export async function get<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const headers = options.skipAuth ? {} : await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/${normalizeUrl(url)}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
  return handleResponse(response);
}

export async function post<T>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
  const isFormData = data instanceof FormData;
  const headers = options.skipAuth ? {} : await getAuthHeaders(isFormData);
  
  // Merge headers, but don't override Content-Type if it's explicitly set to undefined
  const mergedHeaders: HeadersInit = { ...headers };
  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (value === undefined) {
        delete mergedHeaders[key];
      } else {
        mergedHeaders[key] = value;
      }
    });
  }
  
  const response = await fetch(`${API_BASE_URL}/${normalizeUrl(url)}`, {
    method: 'POST',
    ...options,
    headers: mergedHeaders,
    body: isFormData ? data : JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function patch<T>(url: string, data: any, options: RequestOptions = {}): Promise<T> {
  const headers = options.skipAuth ? {} : await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/${normalizeUrl(url)}`, {
    method: 'PATCH',
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function downloadFile(url: string, options: RequestOptions = {}): Promise<{ blob: Blob; headers: Headers }> {
  const token = await acquireAccessToken();
  const headers = options.skipAuth ? {} : {
    'Authorization': `Bearer ${token}`,
    'Accept': '*/*'
  };
  
  const response = await fetch(`${API_BASE_URL}/${normalizeUrl(url)}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    
    // If we have error data from server, enhance it with status and throw
    if (errorData) {
      const apiError = errorData as ApiError;
      apiError.status = response.status;
      throw apiError;
    }
    
    // Fallback for non-JSON errors
    const error = new Error(`Failed to download file. Status: ${response.status}`) as ApiError;
    error.status = response.status;
    throw error;
  }
  
  const blob = await response.blob();
  return { blob, headers: response.headers };
}

export async function deleteRequest<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const headers = options.skipAuth ? {} : await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/${normalizeUrl(url)}`, {
    method: 'DELETE',
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
  return handleResponse(response);
}

const apiClient = {
  get,
  post,
  patch,
  delete: deleteRequest,
  downloadFile,
};

export default apiClient;
